package com.clany.backoffice.data.network

import com.clany.backoffice.data.local.TokenStorage
import com.clany.backoffice.data.network.response.ServiceAuthEntity
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.auth.Auth
import io.ktor.client.plugins.auth.providers.BearerTokens
import io.ktor.client.plugins.auth.providers.bearer
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.logging.DEFAULT
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logger
import io.ktor.client.plugins.logging.Logging
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.request.header
import io.ktor.client.request.request
import io.ktor.client.request.setBody
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import io.ktor.serialization.kotlinx.json.json
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.json.Json

import dev.zacsweers.metro.Inject
import com.clany.backoffice.di.AppScope
import dev.zacsweers.metro.SingleIn

@SingleIn(AppScope::class)
@Inject
class NetworkClient(private val tokenStorage: TokenStorage) {

    private val json = Json {
        prettyPrint = true
        isLenient = true
        ignoreUnknownKeys = true
    }

    // Client for unauthenticated calls (Login, Refresh)
    @PublishedApi
    internal val publicClient = HttpClient {
        expectSuccess = false
        install(ContentNegotiation) {
            json(json)
        }
        install(Logging) {
            logger = Logger.DEFAULT
            level = LogLevel.NONE
        }
    }

    val httpClient = HttpClient {
        install(ContentNegotiation) {
            json(json)
        }
        install(Logging) {
            logger = Logger.DEFAULT
            level = LogLevel.NONE
        }

        install(Auth) {
            bearer {
                loadTokens {
                    BearerTokens(
                        accessToken = tokenStorage.getToken() ?: "",
                        refreshToken = tokenStorage.getRefreshToken() ?: ""
                    )
                }
                sendWithoutRequest { true }
                refreshTokens {
                    val refreshToken = tokenStorage.getRefreshToken()
                    if (refreshToken.isNullOrBlank()) return@refreshTokens null

                    try {
                        // Manual refresh call to avoid infinite loop
                        val response: ServiceAuthEntity = publicClient.request(LOGIN_URL) {
                            method = HttpMethod.Post
                            setBody(AuthBodyCalls.refreshTokenBody(refreshToken, CLIENT_ID))
                            contentType(ContentType.Application.FormUrlEncoded)
                        }.body()

                        if (response.accessToken != null && response.refreshToken != null) {
                            tokenStorage.saveToken(response.accessToken)
                            tokenStorage.saveRefreshToken(response.refreshToken)
                            tokenStorage.saveExpiresIn(response.expiresIn?.toLong() ?: 0L)
                            tokenStorage.saveRefreshExpiresIn(response.refreshExpiresIn?.toLong() ?: 0L)

                            BearerTokens(
                                accessToken = response.accessToken,
                                refreshToken = response.refreshToken
                            )
                        } else {
                            tokenStorage.clearAll()
                            null
                        }
                    } catch (e: Exception) {
                        if (e is CancellationException) throw e
                        tokenStorage.clearAll()
                        null
                    }
                }
            }
        }

        defaultRequest {
            url(API_URL_BASE)
            contentType(ContentType.Application.Json)
        }
    }

    suspend inline fun <reified T> request(
        endpoint: String,
        method: HttpMethod = HttpMethod.Get,
        body: Any? = null,
        crossinline block: HttpRequestBuilder.() -> Unit = {}
    ): Result<T> {
        return try {
            val response = httpClient.request(endpoint) {
                this.method = method
                if (body != null) {
                    setBody(body)
                }
                block()
            }
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception("HTTP ${response.status.value}: ${response.status.description}"))
            }
        } catch (e: Throwable) {
            if (e is CancellationException) throw e
            Result.failure(e)
        }
    }
    
    suspend inline fun <reified T> publicRequest(
        url: String,
        method: HttpMethod = HttpMethod.Get,
        body: Any? = null,
        isFormUrlEncoded: Boolean = false
    ): Result<T> {
        return try {
            val response = publicClient.request(url) {
                this.method = method
                if (isFormUrlEncoded) {
                    contentType(ContentType.Application.FormUrlEncoded)
                } else {
                    contentType(ContentType.Application.Json)
                }
                
                if (body != null) {
                    setBody(body)
                }
            }
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception("HTTP ${response.status.value}: ${response.status.description}"))
            }
        } catch (e: Throwable) {
            if (e is CancellationException) throw e
            Result.failure(e)
        }
    }
}
